import { useCallback, useEffect, useState } from "react";
import { Button, Form, InputNumber, message, Modal, Popconfirm, Select, Space, Switch, Table, Tag } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import service from "../../api/FetchInterceptor";
import { formatMoney, PROVIDER_LABELS } from "../../lib/money";

const FormationPrices = ({ formationId, onChange }) => {
    const [form] = Form.useForm();
    const [prices, setPrices] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editPrice, setEditPrice] = useState(null);
    const [saving, setSaving] = useState(false);

    const selectedCurrency = Form.useWatch("currency", form);
    const currencyInfo = currencies.find((c) => c.code === selectedCurrency);

    const load = useCallback(() => {
        setLoading(true);
        service({ method: "GET", url: `admin/formations/${formationId}/prices` })
            .then(setPrices)
            .finally(() => setLoading(false));
    }, [formationId]);

    useEffect(() => {
        load();
        service({ method: "GET", url: "admin/formations/create" }).then((data) => setCurrencies(data.currencies ?? []));
    }, [load]);

    const changed = () => {
        load();
        onChange?.();
    };

    const openModal = (price = null) => {
        setEditPrice(price);
        form.setFieldsValue(
            price
                ? { currency: price.currency, amount: price.amount_major, is_active: price.is_active }
                : { currency: undefined, amount: null, is_active: true }
        );
        setModalOpen(true);
    };

    const save = (values) => {
        setSaving(true);
        service({
            method: editPrice ? "PUT" : "POST",
            url: editPrice
                ? `admin/formations/${formationId}/prices/${editPrice.id}`
                : `admin/formations/${formationId}/prices`,
            data: values,
        })
            .then(() => {
                message.success("Prix enregistré");
                setModalOpen(false);
                changed();
            })
            .catch((e) => {
                const errors = e?.response?.data?.errors;
                message.error(errors ? Object.values(errors).flat()[0] : "Erreur lors de l'enregistrement");
            })
            .finally(() => setSaving(false));
    };

    const toggleActive = (price, is_active) => {
        service({
            method: "PUT",
            url: `admin/formations/${formationId}/prices/${price.id}`,
            data: { currency: price.currency, amount: price.amount_major, is_active },
        })
            .then(changed)
            .catch(() => message.error("Erreur lors de la mise à jour"));
    };

    const remove = (price) => {
        service({ method: "DELETE", url: `admin/formations/${formationId}/prices/${price.id}` })
            .then(() => {
                message.success("Prix supprimé");
                changed();
            })
            .catch(() => message.error("Erreur lors de la suppression"));
    };

    // Une devise déjà utilisée ne peut pas être choisie une 2e fois.
    const usedCurrencies = prices.filter((p) => p.id !== editPrice?.id).map((p) => p.currency);

    const columns = [
        {
            key: "currency",
            dataIndex: "currency",
            title: "Devise",
            render: (code) => {
                const c = currencies.find((x) => x.code === code);
                return (
                    <div>
                        <div className="font-medium">{code}</div>
                        {c && <div className="text-xs text-gray-500">{c.label}</div>}
                    </div>
                );
            },
        },
        {
            key: "amount",
            title: "Montant",
            render: (_, record) => <span className="font-medium">{formatMoney(record.amount_major, record.currency)}</span>,
        },
        {
            key: "provider",
            dataIndex: "provider",
            title: "Paiement via",
            render: (provider) => <Tag color={provider === "fedapay" ? "orange" : provider === "square" ? "cyan" : "geekblue"}>{PROVIDER_LABELS[provider] ?? provider}</Tag>,
        },
        {
            key: "is_active",
            dataIndex: "is_active",
            title: "Actif",
            render: (active, record) => <Switch checked={active} onChange={(v) => toggleActive(record, v)} />,
        },
        {
            key: "actions",
            width: 110,
            render: (_, record) => (
                <Space>
                    <Button type="text" icon={<EditOutlined />} title="Modifier" onClick={() => openModal(record)} />
                    <Popconfirm
                        title="Supprimer ce prix ?"
                        okText="Supprimer"
                        cancelText="Annuler"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => remove(record)}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} title="Supprimer" />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Modal
                title={editPrice ? "Modifier le prix" : "Ajouter un prix"}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText="Enregistrer"
                cancelText="Annuler"
                confirmLoading={saving}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={save} preserve={false}>
                    <Form.Item name="currency" label="Devise" rules={[{ required: true, message: "Choisissez une devise" }]}>
                        <Select
                            placeholder="Choisir une devise"
                            options={currencies.map((c) => ({
                                value: c.code,
                                label: `${c.code} — ${c.label} (${PROVIDER_LABELS[c.provider] ?? c.provider})`,
                                disabled: usedCurrencies.includes(c.code),
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="amount"
                        label="Montant"
                        rules={[{ required: true, message: "Indiquez un montant" }]}
                        extra={currencyInfo?.zero_decimal ? "Montant entier, sans centimes (ex : 25000)." : "Ex : 49.90"}
                    >
                        <InputNumber
                            min={0}
                            precision={currencyInfo?.zero_decimal ? 0 : 2}
                            step={currencyInfo?.zero_decimal ? 500 : 1}
                            addonAfter={selectedCurrency}
                            style={{ width: "100%" }}
                        />
                    </Form.Item>

                    <Form.Item name="is_active" label="Actif" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            <div className="mb-4 flex items-center justify-between">
                <p className="m-0 text-sm text-gray-500">
                    Un prix par devise. Le moyen de paiement dépend de la devise.
                </p>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
                    Ajouter un prix
                </Button>
            </div>

            <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={prices}
                pagination={false}
                locale={{ emptyText: "Aucun prix — la formation ne pourra pas être achetée" }}
            />
        </>
    );
};

export default FormationPrices;
